import React, { useState, useCallback } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { ChevronDown, ChevronUp, Paperclip, Trash2, X } from 'lucide-react';

import { OrderSchemaType } from '../order.schema';
import { useAttachmentHandler } from '../Hooks/useAttachmentHandler';
import InfiniteSelect from '../../../../../components/InfiniteSelect';
import ApiService from '../../../../../services/ApiService';

import {
  Title,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Textarea,
  CollapsibleHeader,
  CircularCollapseIcon,
  Separator,
  AttachmentList,
  AttachmentListItem,
  RemoveAttachmentButton,
  AttachmentInputContainer,
  AttachmentTrigger,
  AttachmentPlaceholder,
  TrashIconContainer,
  SwitchBox,
  SwitchInput,
  SwitchSlider
} from '../style';

const DetailsSection: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const { control, register, setValue, watch, formState: { errors } } = useFormContext<OrderSchemaType>();

  const {
    attachments,
    attachmentError,
    fileInputRef,
    handleFileSelectClick,
    handleFileChange,
    handleRemoveAttachment,
    handleRemoveAllAttachments,
  } = useAttachmentHandler({ fieldPath: 'attachments' });

  const fetchOrderTypes = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1;
    const response = await ApiService.steelOrders.getOrderTypes({ page, limit, search });
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  }, []);

  const createOrderTypeOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createOrderType({ name });
    return response.data;
  }, []);

  const fetchRepresentatives = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1;
    const response = await ApiService.steelOrders.getRepresentatives({ page, limit, search });
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  }, []);

  const createRepresentativeOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createRepresentative({ name });
    return response.data;
  }, []);

  const fetchOrderSituations = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1;
    const response = await ApiService.steelOrders.getOrderSituations({ page, limit, search });
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  }, []);

  const createOrderSituationOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createOrderSituation({ name });
    return response.data;
  }, []);

  const fetchGeneralClasses = useCallback(async (limit: number, offset: number, search?: string) => {
    const page = Math.floor(offset / limit) + 1;
    const response = await ApiService.steelOrders.getGeneralClasses({ page, limit, search });
    return Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  }, []);

  const createGeneralClassOption = useCallback(async (name: string) => {
    const response = await ApiService.steelOrders.createGeneralClass({ name });
    return response.data;
  }, []);

  const deleteOrderTypeOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteOrderType(Number(id));
  }, []);

  const deleteRepresentativeOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteRepresentative(Number(id));
  }, []);

  const deleteOrderSituationOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteOrderSituation(Number(id));
  }, []);

  const deleteGeneralClassOption = useCallback(async (id: any) => {
    await ApiService.steelOrders.deleteGeneralClass(Number(id));
  }, []);

  const orderTypeId = watch('orderTypeId');
  const orderTypeName = watch('orderType');
  const selectedOrderType = orderTypeId ? { id: orderTypeId, name: String(orderTypeName ?? '') } : null;

  const representativeId = watch('representativeId');
  const representativeName = watch('representativeName');
  const selectedRepresentative = representativeId ? { id: representativeId, name: String(representativeName ?? '') } : null;

  const orderSituationId = watch('orderSituationId');
  const orderSituationName = watch('orderSituationName');
  const selectedOrderSituation = orderSituationId ? { id: orderSituationId, name: String(orderSituationName ?? '') } : null;

  const generalClassId = watch('generalClassId');
  const generalClassName = watch('generalClassName');
  const selectedGeneralClass = generalClassId ? { id: generalClassId, name: String(generalClassName ?? '') } : null;

  const handleSelectOrderType = useCallback((option: any) => {
    setValue('orderTypeId', option?.id, { shouldDirty: true });
    setValue('orderType', option?.name ?? '', { shouldDirty: true });
  }, [setValue]);

  const handleSelectRepresentative = useCallback((option: any) => {
    setValue('representativeId', option?.id, { shouldDirty: true });
    setValue('representativeName', option?.name ?? '', { shouldDirty: true });
  }, [setValue]);

  const handleSelectOrderSituation = useCallback((option: any) => {
    setValue('orderSituationId', option?.id, { shouldDirty: true });
    setValue('orderSituationName', option?.name ?? '', { shouldDirty: true });
  }, [setValue]);

  const handleSelectGeneralClass = useCallback((option: any) => {
    setValue('generalClassId', option?.id, { shouldDirty: true });
    setValue('generalClassName', option?.name ?? '', { shouldDirty: true });
  }, [setValue]);

  return (
    <>
      <CollapsibleHeader onClick={() => setIsDetailsOpen(prev => !prev)}>
        <Title>Detalhes do pedido</Title>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsDetailsOpen(prev => !prev); }}>
          {isDetailsOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isDetailsOpen && (
        <>
          <FormGrid columns={3}>
            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Tipo de Pedido"
                value={selectedOrderType}
                fetchFunction={fetchOrderTypes}
                onItemCreate={createOrderTypeOption}
                onItemDelete={deleteOrderTypeOption}
                onSelect={handleSelectOrderType}
                hasError={!!get(errors, 'orderTypeId')}
              />
            </FormGroup>

            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Representante"
                value={selectedRepresentative}
                fetchFunction={fetchRepresentatives}
                onItemCreate={createRepresentativeOption}
                onItemDelete={deleteRepresentativeOption}
                onSelect={handleSelectRepresentative}
                hasError={!!get(errors, 'representativeId')}
              />
            </FormGroup>

            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Situação do Pedido"
                value={selectedOrderSituation}
                fetchFunction={fetchOrderSituations}
                onItemCreate={createOrderSituationOption}
                onItemDelete={deleteOrderSituationOption}
                onSelect={handleSelectOrderSituation}
                hasError={!!get(errors, 'orderSituationId')}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={3}>
            <FormGroup>
              <InfiniteSelect
                mode="crud"
                allowDelete={true}
                getOptionLabel={(opt: any) => opt.name}
                label="Classe Geral"
                value={selectedGeneralClass}
                fetchFunction={fetchGeneralClasses}
                onItemCreate={createGeneralClassOption}
                onItemDelete={deleteGeneralClassOption}
                onSelect={handleSelectGeneralClass}
                hasError={!!get(errors, 'generalClassId')}
              />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup>
              <Label htmlFor="attachments">Anexos</Label>
              <AttachmentInputContainer>
                <AttachmentTrigger onClick={handleFileSelectClick} hasError={!!attachmentError}>
                  <Paperclip size={16} />
                  <AttachmentPlaceholder>Clique para anexar arquivo</AttachmentPlaceholder>
                </AttachmentTrigger>
                {attachments.length > 0 && (
                  <TrashIconContainer
                    as="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveAllAttachments();
                    }}
                  >
                    <Trash2 size={16} />
                  </TrashIconContainer>
                )}
              </AttachmentInputContainer>
              {attachments.length > 0 && (
                <AttachmentList>
                  {attachments.map((file, index) => (
                    <AttachmentListItem key={index}>
                      {file.name}
                      <RemoveAttachmentButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemoveAttachment(index);
                        }}
                      >
                        <X size={12} />
                      </RemoveAttachmentButton>
                    </AttachmentListItem>
                  ))}
                </AttachmentList>
              )}
              <input
                type="file"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept=".pdf,.xml,.doc,.docx,.xls,.xlsx,.jpeg,.jpg,.png"
              />
              {attachmentError && <p style={{ color: 'red', fontSize: '12px' }}>{attachmentError}</p>}
            </FormGroup>

            <FormGroup>
              <Label>Informação extra</Label>
              <Input {...register('extraInfo')} placeholder="Digite" />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={3}>
            <FormGroup style={{ alignItems: 'flex-start' }}>
              <Label htmlFor="criticalAnalysis">Análise Crítica</Label>
              <Controller
                name="criticalAnalysis"
                control={control}
                render={({ field }) => (
                  <SwitchBox>
                    <SwitchInput
                      type="checkbox"
                      id="criticalAnalysis"
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                    <SwitchSlider className="slider"></SwitchSlider>
                  </SwitchBox>
                )}
              />
            </FormGroup>

            <FormGroup style={{ gridColumn: 'span 2' }}>
              <Label>Descrição da Análise</Label>
              <Textarea
                {...register('criticalAnalysisDesc')}
                placeholder="Digite a descrição da análise"
              />
            </FormGroup>
          </FormGrid>
        </>
      )}
      <Separator />
    </>
  );
};

export default DetailsSection;